import {Injectable, NgZone} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {AppConfig} from '../../app/app.config';
import {ClientInstanceService} from './client-instance.service';
import {AuthService} from '../../_foundation/services/auth/auth.service';

export interface DataChangeNotification {
  vesselId: number;
  area: string;
  entityType: string;
  entityId: string;
  changeType: string;
  revision: string;
  dailyLogDate?: string | null;
  changedAtUtc: string;
  changedByUserId?: number | null;
  changedByDisplayName?: string | null;
  clientInstanceId?: string | null;
}

@Injectable()
export class SignalRService {
  private readonly changesSubject = new Subject<DataChangeNotification>();
  readonly changes$: Observable<DataChangeNotification> = this.changesSubject.asObservable();

  private connection: any;
  private proxy: any;
  private activeVesselId: number | null = null;
  private subscribedVesselId: number | null = null;
  private subscribingVesselId: number | null = null;
  private subscriptionPromise: Promise<void> | null = null;
  private transition: Promise<void> = Promise.resolve();
  private notificationHandler: (notification: any) => void;

  constructor(
    private clientInstance: ClientInstanceService,
    private authService: AuthService,
    private zone: NgZone
  ) {}

  get clientInstanceId(): string {
    return this.clientInstance.id;
  }

  /** Starts (or reuses) the independent data-change connection and subscribes it to a vessel. */
  connect(vesselId: number): Promise<void> {
    if (!vesselId) {
      return Promise.resolve();
    }

    this.transition = this.transition
      .catch(() => undefined)
      .then(() => this.connectInternal(vesselId));

    return this.transition;
  }

  /** Explicitly stops only this service's connection; it never touches $.connection.hub. */
  disconnect(): Promise<void> {
    this.transition = this.transition
      .catch(() => undefined)
      .then(() => {
        this.activeVesselId = null;
        this.subscribedVesselId = null;
        this.subscribingVesselId = null;
        this.subscriptionPromise = null;
        if (this.connection && this.connection.state !== this.connectionStates.disconnected) {
          this.connection.stop();
        }
      });

    return this.transition;
  }

  private async connectInternal(vesselId: number): Promise<void> {
    this.ensureConnection();

    const vesselChanged = this.activeVesselId != null && this.activeVesselId !== vesselId;
    this.activeVesselId = vesselId;

    // Stopping removes the old server-side group membership. The same connection and handlers
    // are then reused for the new vessel, avoiding both leaked groups and duplicate handlers.
    if (vesselChanged && this.connection.state !== this.connectionStates.disconnected) {
      this.subscribedVesselId = null;
      this.subscribingVesselId = null;
      this.subscriptionPromise = null;
      this.connection.stop();
    }

    if (this.connection.state === this.connectionStates.connected) {
      await this.subscribeToActiveVessel();
      return;
    }

    if (this.connection.state === this.connectionStates.connecting ||
        this.connection.state === this.connectionStates.reconnecting) {
      await this.waitUntilConnected();
      await this.subscribeToActiveVessel();
      return;
    }

    await this.startConnection();
    await this.subscribeToActiveVessel();
  }

  private ensureConnection(): void {
    if (this.connection) {
      return;
    }

    const url = `${AppConfig.settings.apiUrl}api/signalr`;
    this.connection = ($ as any).hubConnection(url, {useDefaultPath: false});
    this.refreshAccessToken();
    this.proxy = this.connection.createHubProxy('DataChangeHub');

    this.notificationHandler = (notification: any) => {
      const normalizedNotification = this.normalizeNotification(notification);
      if (normalizedNotification) {
        this.zone.run(() => this.changesSubject.next(normalizedNotification));
      }
    };
    this.proxy.on('DataChanged', this.notificationHandler);

    this.connection.reconnected(() => {
      // SignalR creates a new server connection after reconnecting, so group membership must be restored.
      this.subscribedVesselId = null;
      this.subscribeToActiveVessel().catch(err => console.error('Unable to resubscribe to vessel changes', err));
    });
    this.connection.disconnected(() => this.subscribedVesselId = null);
  }

  private startConnection(): Promise<void> {
    this.refreshAccessToken();
    return new Promise<void>((resolve, reject) => {
      this.connection
        .start({skipNegotiation: false, transport: ['serverSentEvents', 'longPolling']} as any)
        .done(() => resolve())
        .fail(error => reject(error));
    });
  }

  private subscribeToActiveVessel(): Promise<void> {
    if (!this.activeVesselId || this.subscribedVesselId === this.activeVesselId || !this.proxy ||
        this.connection.state !== this.connectionStates.connected) {
      return Promise.resolve();
    }

    const vesselId = this.activeVesselId;
    if (this.subscribingVesselId === vesselId && this.subscriptionPromise) {
      return this.subscriptionPromise;
    }

    this.subscribingVesselId = vesselId;
    const invocation = new Promise<void>((resolve, reject) => {
      this.proxy.invoke('SubscribeToVessel', vesselId)
        .done(() => {
          if (this.activeVesselId === vesselId) {
            this.subscribedVesselId = vesselId;
          }
          resolve();
        })
        .fail(error => reject(error));
    });

    const tracked = invocation.then(
      () => {
        if (this.subscriptionPromise === tracked) {
          this.subscribingVesselId = null;
          this.subscriptionPromise = null;
        }
      },
      error => {
        if (this.subscriptionPromise === tracked) {
          this.subscribingVesselId = null;
          this.subscriptionPromise = null;
        }
        throw error;
      }
    );
    this.subscriptionPromise = tracked;
    return tracked;
  }

  private waitUntilConnected(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error('SignalR connection timed out')), 30000);
      const check = (change: any) => {
        if (change.newState === this.connectionStates.connected) {
          window.clearTimeout(timeout);
          resolve();
        } else if (change.newState === this.connectionStates.disconnected) {
          window.clearTimeout(timeout);
          reject(new Error('SignalR connection disconnected'));
        }
      };
      this.connection.stateChanged(check);
    });
  }

  private get connectionStates(): any {
    return ($ as any).signalR.connectionState;
  }

  private refreshAccessToken(): void {
    if (!this.connection) {
      return;
    }
    const authHeader = this.authService.getAuthHeader() || '';
    const token = authHeader.replace(/^Bearer\s+/i, '').trim();
    if (token && token !== 'null') {
      // jQuery SignalR 2.4.2 uses accessToken for the negotiate request and transport connection.
      this.connection.accessToken = token;
    } else {
      this.connection.accessToken = null;
    }
  }

  /**
   * SignalR publishers can use either the configured camel-case contract or the C# DTO's
   * Pascal-case property names. Normalize once at the hub boundary so all consumers use
   * the same shape and comparison rules.
   */
  private normalizeNotification(rawNotification: any): DataChangeNotification | null {
    if (!rawNotification) {
      return null;
    }

    let notification = rawNotification;
    if (typeof notification === 'string') {
      try {
        notification = JSON.parse(notification);
      } catch (error) {
        console.error('Unable to parse data-change notification', error);
        return null;
      }
    }

    return {
      vesselId: this.notificationValue(notification, 'vesselId', 'VesselId'),
      area: this.notificationValue(notification, 'area', 'Area'),
      entityType: this.notificationValue(notification, 'entityType', 'EntityType'),
      entityId: this.notificationValue(notification, 'entityId', 'EntityId'),
      changeType: this.notificationValue(notification, 'changeType', 'ChangeType'),
      revision: this.notificationValue(notification, 'revision', 'Revision'),
      dailyLogDate: this.notificationValue(notification, 'dailyLogDate', 'DailyLogDate'),
      changedAtUtc: this.notificationValue(notification, 'changedAtUtc', 'ChangedAtUtc'),
      changedByUserId: this.notificationValue(notification, 'changedByUserId', 'ChangedByUserId'),
      changedByDisplayName: this.notificationValue(notification, 'changedByDisplayName', 'ChangedByDisplayName'),
      clientInstanceId: this.notificationValue(notification, 'clientInstanceId', 'ClientInstanceId')
    };
  }

  private notificationValue(notification: any, camelCaseName: string, pascalCaseName: string): any {
    return notification[camelCaseName] !== undefined
      ? notification[camelCaseName]
      : notification[pascalCaseName];
  }

}



// WEBPACK FOOTER //
// ./src/services/signalR/signalRService.ts